import React from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import { BatchStaleness } from "../../../models/BatchStaleness";

interface Props {
	staleness?: BatchStaleness;
	onRefresh: () => void;
	isRefreshing?: boolean;
}

/**
 * Amber callout shown on a draft batch when the underlying time reports
 * changed after its preview was computed, prompting an in-place refresh.
 * Renders nothing while the preview is fresh.
 */
const BatchStalenessBanner: React.FC<Props> = ({ staleness, onRefresh, isRefreshing }) => {
	const { t } = useTranslation();

	if (!staleness?.stale) return null;

	const details = [
		staleness.addedTimeReports > 0 &&
			t("batchStaleness.added", { count: staleness.addedTimeReports }),
		staleness.changedTimeReports > 0 &&
			t("batchStaleness.changed", { count: staleness.changedTimeReports }),
		staleness.removedTimeReports > 0 &&
			t("batchStaleness.removed", { count: staleness.removedTimeReports }),
	]
		.filter(Boolean)
		.join(", ");

	return (
		<div className="mb-3 flex items-center justify-between gap-4 rounded-md border-2 border-amber-400 bg-amber-50 p-4">
			<div>
				<p className="text-lg font-medium text-amber-700">{t("batchStaleness.title")}</p>
				<p className="mt-1 text-sm">{t("batchStaleness.info", { details })}</p>
			</div>
			<button
				type="button"
				onClick={onRefresh}
				disabled={isRefreshing}
				className="flex shrink-0 items-center gap-1.5 rounded-md bg-amber-500 px-3 py-2 text-sm font-medium text-white hover:bg-amber-600 disabled:cursor-not-allowed disabled:opacity-60"
			>
				<RefreshCw size={14} className={isRefreshing ? "animate-spin" : undefined} />
				{t("batchRefresh.button")}
			</button>
		</div>
	);
};

export default BatchStalenessBanner;
